import { BriefCreatorTypeEnum } from "@nlc/shared-types";
import { useIsMutating } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";

import { Alert } from "@/components/alert";
import { Radio, RadioGroup, Radios } from "@/components/radio-group";
import {
  formTypeMap,
  useCapturePosthogEvent,
} from "@/hooks/posthog/useCapturePosthogEvent";

import { useCreateWebsiteDesignRequestFormContext } from "../useCreateWebsiteDesignRequestForm";
import { useAlert } from "./hooks/useAlert";
type TabsHeaderProps = {
  isDisabled: boolean;
};

export function TabsHeader({ isDisabled }: TabsHeaderProps) {
  const methods = useCreateWebsiteDesignRequestFormContext();
  const formType = methods.watch("briefCreatorType");
  const capture = useCapturePosthogEvent();

  const { hideAlert, isAlertHidden } = useAlert("check-mbf");

  const isPending = Boolean(
    useIsMutating({
      mutationKey: ["createOutputEnhancement", "website-design"],
      status: "pending",
    }),
  );

  const isBlocked = isDisabled || isPending;

  const options = [
    {
      description: "Legacy version of brief creator",
      name: "Classic Brief Form",
      value: BriefCreatorTypeEnum.SINGLE,
    },
    {
      description: "Splited into detailed categories",
      name: "Magical Brief Form",
      value: BriefCreatorTypeEnum.DETAILED,
    },
  ] as const;

  return (
    <div className="col-span-1 row-span-1 flex w-full flex-col gap-[10px]">
      <span className="text-lg font-semibold text-text-secondary">
        Brief Creator
      </span>
      {isAlertHidden ? null : (
        <Alert
          description="Check the new Magical Brief Form and let the AI help you!"
          onClick={hideAlert}
          title="Creating your request from scratch?"
        />
      )}
      <RadioGroup
        aria-label="Brief creator type"
        onChange={(value) => {
          const newType = value as BriefCreatorTypeEnum;

          capture("form_type_switch", {
            switched_from: formTypeMap[formType],
            switched_to: formTypeMap[newType],
          });

          methods.setValue("briefCreatorType", newType);
        }}
        value={formType}
      >
        <Radios className="grid grid-cols-2 gap-[10px]">
          {options.map((option) => (
            <Radio
              aria-label={option.name}
              className={({ isSelected }) =>
                twMerge(
                  "items-start rounded-xl border border-border-primary p-xl [&_[slot=radio]]:mt-[3px]",
                  isSelected &&
                    "border-border-brand-solid ring-2 ring-border-brand-solid disabled:border-border-disabled-subtle disabled:ring-border-disabled-subtle",
                  "disabled:bg-bg-disabled-subtle",
                )
              }
              isDisabled={isBlocked}
              key={option.value}
              value={option.value}
            >
              <div className="flex flex-1 flex-col gap-0">
                <div className="text-sm font-medium text-text-secondary">
                  {option.name}
                </div>
                <div className="text-sm text-text-tertiary">
                  {option.description}
                </div>
              </div>
            </Radio>
          ))}
        </Radios>
      </RadioGroup>
    </div>
  );
}
